import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

/* ===================================================
   AUTH — one source of truth for "is this browser logged in?".

   The real session is the HttpOnly cookie (JS can't read it), so the ONLY way to know
   whether it is still valid is to ask the server: GET /api/profile. Previously every page
   trusted a localStorage flag instead, which can say "logged in" with a dead cookie, or
   "logged out" with a valid one (e.g. after a transient 401 cleared it) -- and the login
   page never noticed an existing session. Now:

     * AuthProvider validates the session with /api/profile on load, on tab focus (throttled),
       when another tab logs in/out, and whenever a request reports 401.
     * ProtectedRoute waits for that check, then renders the page or redirects to /login.
     * PublicOnlyRoute keeps logged-in users off /login and /signup.
     * A network / 5xx failure is NOT treated as "logged out" -- the user is never kicked out
       just because the server was briefly unreachable.

   The localStorage flag ("subai_session") is kept only as a non-sensitive UI hint for legacy
   pages; it is re-synced from the server's answer here.
=================================================== */

const FLAG = "subai_session";
const REVALIDATE_AFTER_MS = 60 * 1000;

const setFlag = (on) => {
  try { if (on) localStorage.setItem(FLAG, "1"); else localStorage.removeItem(FLAG); } catch { /* storage blocked */ }
};

const AuthContext = createContext({
  status: "loading", user: null,
  refresh: async () => "loading", logout: async () => false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null });   // loading | authed | anon | error
  const inflight  = useRef(null);
  const lastCheck = useRef(0);

  const refresh = useCallback(() => {
    if (inflight.current) return inflight.current;            // overlapping checks (StrictMode, bursts of 401s) share one request
    inflight.current = (async () => {
      try {
        const res = await fetch("/api/profile", { credentials: "include", cache: "no-store" });
        lastCheck.current = Date.now();
        if (res.ok) {
          let user = null;
          try { user = await res.json(); } catch { user = null; }
          setFlag(true);
          setState({ status: "authed", user });
          return "authed";
        }
        if (res.status === 401 || res.status === 404) {        // no valid session / account gone
          setFlag(false);
          setState({ status: "anon", user: null });
          return "anon";
        }
        throw new Error("profile " + res.status);              // 5xx / 429: unknown, not "logged out"
      } catch {
        let result = "error";
        setState((prev) => {
          if (prev.status === "authed") { result = "authed"; return prev; }   // keep the user in; try again later
          return { status: "error", user: null };
        });
        return result;
      } finally {
        inflight.current = null;
      }
    })();
    return inflight.current;
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => {
      if (document.visibilityState === "visible" && Date.now() - lastCheck.current > REVALIDATE_AFTER_MS) refresh();
    };
    const onUnauthorized = () => { refresh(); };
    const onStorage = (e) => { if (e.key === FLAG) refresh(); };   // another tab logged in / out
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    window.addEventListener("subai:unauthorized", onUnauthorized);
    window.addEventListener("storage", onStorage);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("subai:unauthorized", onUnauthorized);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  // Resolves true only if the server really cleared the cookie. If it could not be reached the user
  // stays logged in (and the caller can say so) instead of showing a fake "logged out" state.
  const logout = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
      if (!res.ok) return false;
    } catch { return false; }
    try { localStorage.removeItem("token"); } catch { /* ignore */ }
    try {
      // API keys typed for a run are kept in sessionStorage for refresh-restore only -- drop them on logout.
      Object.keys(sessionStorage).filter((k) => k.startsWith("subai_session_")).forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
    setFlag(false);
    setState({ status: "anon", user: null });
    return true;
  }, []);

  const value = useMemo(() => ({ ...state, refresh, logout }), [state, refresh, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function FullPageStatus({ text, onRetry }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0B0B10", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 24, fontFamily: "var(--font-ui)" }}>
      <p style={{ color: "#A8A8B3", fontSize: 13, margin: 0, textAlign: "center" }}>{text}</p>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: "11px 22px", background: "#7C5CFF", border: "none", borderRadius: 9999, color: "#F5F5F7", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
          Retry
        </button>
      )}
    </div>
  );
}

/* Renders `children` only for a validated session; otherwise sends the user to /login
   (remembering where they were going). */
export function ProtectedRoute({ children }) {
  const { status, refresh } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageStatus text="Session check ho raha hai..." />;
  if (status === "error")   return <FullPageStatus text="Server se connect nahi ho paaya. Internet check karo." onRetry={refresh} />;
  if (status !== "authed")  return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

/* /login and /signup: a user who already has a valid session goes straight to the app. */
export function PublicOnlyRoute({ children }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageStatus text="Session check ho raha hai..." />;
  if (status === "authed") {
    const from = location.state?.from;
    const target = from?.pathname && from.pathname !== "/" && from.pathname !== "/login" && from.pathname !== "/signup"
      ? from.pathname + (from.search || "") : "/dashboard";
    return <Navigate to={target} replace />;
  }
  return children;   // anon, or server unreachable: let the person try to log in
}
